// Dashboard de entrada e saída de pessoas nos grupos de WhatsApp (Z-API).
// Sem biblioteca de gráfico: SVG puro. Base: estudo "Dashboard de entrada e saída".
// Modelo: instância -> grupos. Filtra por instância, depois por grupo.
//
//   <DashboardGrupos apiBase="https://.../api" />
//
// Lê GET /group-members/metrics e GET /group-members/events (functions/index.js).

import React, { useEffect, useMemo, useRef, useState } from 'react';

/* ------------------------------------------------------------------ helpers */

const fmt = (n) => (n == null ? '—' : new Intl.NumberFormat('pt-BR').format(n));
const sinal = (n) => (n > 0 ? `+${fmt(n)}` : fmt(n));

function rotuloDia(dia) {
  const [, m, d] = dia.split('-');
  return `${d}/${m}`;
}

// Dia sempre em São Paulo, igual ao backend. toISOString() usaria UTC e,
// depois das 21h, "hoje" viraria amanhã.
const fmtDiaSP = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
});
const diasAtras = (n) => fmtDiaSP.format(new Date(Date.now() - n * 864e5));
const HOJE = () => diasAtras(0);

const PRESETS = [
  { id: 7, rotulo: '7 dias' },
  { id: 30, rotulo: '30 dias' },
  { id: 90, rotulo: '90 dias' },
];

const LIMITE_EVENTOS = 300;
const SEM_INSTANCIA = 'sem-instancia';

function fmtTelefone(t) {
  const m = String(t || '').match(/^55(\d{2})(\d{4,5})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : t;
}

// Apelido (campo `nome` no Firestore) > número conectado > final do id
function rotuloInstancia(inst) {
  if (!inst || inst.instanceId === SEM_INSTANCIA) return 'Sem instância';
  if (inst.nome) return inst.nome;
  if (inst.telefone) return `Instância ${fmtTelefone(inst.telefone)}`;
  return `Instância …${String(inst.instanceId).slice(-4)}`;
}

async function getJson(url) {
  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) throw new Error(data.error || `Erro HTTP ${res.status}`);
  return data;
}

/* -------------------------------------------------------------------- estilo */

const CSS = `
.zg { --surface-1:#fcfcfb; --plane:#f9f9f7;
  --ink:#0b0b0b; --ink-2:#52514e; --muted:#898781;
  --grid:#e1e0d9; --axis:#c3c2b7; --ring:rgba(11,11,11,.10);
  --entrada:#2a78d6; --saida:#eb6834; --saldo:#1baf7a;
  --bom:#0ca30c; --critico:#d03b3b;
  color-scheme:light; background:var(--plane); color:var(--ink);
  font:14px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;
  padding:24px 16px; min-height:100vh; }
@media (prefers-color-scheme: dark){ :root:where(:not([data-theme="light"])) .zg{
  --surface-1:#1a1a19; --plane:#0d0d0d; --ink:#fff; --ink-2:#c3c2b7; --muted:#898781;
  --grid:#2c2c2a; --axis:#383835; --ring:rgba(255,255,255,.10);
  --entrada:#3987e5; --saida:#d95926; --saldo:#199e70; color-scheme:dark; } }
:root[data-theme="dark"] .zg{ --surface-1:#1a1a19; --plane:#0d0d0d; --ink:#fff;
  --ink-2:#c3c2b7; --muted:#898781; --grid:#2c2c2a; --axis:#383835;
  --ring:rgba(255,255,255,.10); --entrada:#3987e5; --saida:#d95926; --saldo:#199e70;
  color-scheme:dark; }

.zg *{box-sizing:border-box}
.zg-wrap{max-width:1120px;margin:0 auto;display:flex;flex-direction:column;gap:16px;min-width:0}
.zg h1{font-size:20px;font-weight:650;margin:0}
.zg h2{font-size:14px;font-weight:600;margin:0;color:var(--ink)}
.zg .sub{font-size:12.5px;color:var(--muted);margin:2px 0 0}

.zg-card{background:var(--surface-1);border:1px solid var(--ring);
  border-radius:12px;padding:16px;min-width:0;overflow:hidden}
.zg-topo{display:flex;flex-wrap:wrap;gap:8px 16px;align-items:flex-start;justify-content:space-between}
.zg-filtros{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.zg-filtros select,.zg-filtros input{background:var(--surface-1);color:var(--ink);
  border:1px solid var(--ring);border-radius:8px;padding:7px 10px;font:inherit;font-size:13px;
  min-width:0;max-width:100%}
.zg-filtros input[type=date]{flex:1 1 140px}
.zg-filtros input[type=search]{flex:1 1 200px}
.zg-filtros select{flex:1 1 200px}
.zg-chip{background:transparent;color:var(--ink-2);border:1px solid var(--ring);
  border-radius:8px;padding:7px 12px;font:inherit;font-size:13px;cursor:pointer}
.zg-chip[aria-pressed="true"]{background:var(--ink);color:var(--surface-1);border-color:var(--ink)}

.zg-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:12px}
.zg-kpi .rot{font-size:12px;color:var(--muted);display:flex;align-items:center;gap:6px}
.zg-kpi .val{font-size:28px;font-weight:600;letter-spacing:-.01em;margin-top:4px}
.zg-kpi .pe{font-size:12px;color:var(--ink-2);margin-top:2px}
.zg-dot{width:8px;height:8px;border-radius:2px;display:inline-block;flex:0 0 auto}

.zg-legenda{display:flex;gap:14px;flex-wrap:wrap;font-size:12.5px;color:var(--ink-2)}
.zg-legenda span{display:flex;align-items:center;gap:6px}

.zg-grid2{display:grid;grid-template-columns:1fr;gap:16px}
@media(min-width:860px){.zg-grid2{grid-template-columns:1fr 1fr}}

.zg-tt{position:absolute;pointer-events:none;z-index:5;background:var(--surface-1);
  border:1px solid var(--ring);border-radius:8px;padding:8px 10px;font-size:12.5px;
  box-shadow:0 6px 20px rgba(0,0,0,.13);white-space:nowrap;color:var(--ink)}
.zg-tt b{font-weight:600}
.zg-tt .l{display:flex;align-items:center;gap:6px;margin-top:3px;color:var(--ink-2)}
.zg-tt .l i{margin-left:auto;font-style:normal;color:var(--ink);font-weight:600;
  font-variant-numeric:tabular-nums;padding-left:14px}

.zg-tab{width:100%;border-collapse:collapse;font-size:13px;min-width:420px}
.zg-tab th{text-align:left;font-weight:600;color:var(--muted);font-size:12px;
  padding:6px 8px;border-bottom:1px solid var(--grid);position:sticky;top:0;background:var(--surface-1)}
.zg-tab td{padding:6px 8px;border-bottom:1px solid var(--grid);
  font-variant-numeric:tabular-nums;color:var(--ink-2)}
.zg-tab td:first-child{color:var(--ink)}
.zg-scroll{max-height:340px;overflow:auto;-webkit-overflow-scrolling:touch}
.zg-scroll.alto{max-height:520px}
.zg-tag{font-size:11.5px;padding:2px 7px;border-radius:99px;border:1px solid var(--ring);
  display:inline-flex;align-items:center;gap:5px;color:var(--ink-2)}
.zg-erro{border-left:3px solid var(--critico);padding-left:12px;color:var(--ink-2)}

.zg-mat{border-collapse:separate;border-spacing:0;font-size:12.5px;
  font-variant-numeric:tabular-nums;min-width:100%}
.zg-mat th,.zg-mat td{padding:6px 8px;border-bottom:1px solid var(--grid);
  text-align:right;white-space:nowrap;color:var(--ink-2)}
.zg-mat thead th{position:sticky;top:0;z-index:2;background:var(--surface-1);
  color:var(--muted);font-weight:600;font-size:11.5px}
.zg-mat .fixa{position:sticky;left:0;z-index:1;background:var(--surface-1);text-align:left;
  max-width:220px;overflow:hidden;text-overflow:ellipsis;border-right:1px solid var(--grid)}
.zg-mat thead .fixa{z-index:3}
.zg-mat .dia{min-width:42px;text-align:center}
.zg-mat td.zero{color:var(--muted);opacity:.5}
.zg-mat td.tot{color:var(--ink);font-weight:600}
.zg-mat tr.inst > *{background:var(--plane);color:var(--ink);font-weight:600}
.zg-link{background:none;border:0;padding:0;font:inherit;color:var(--ink);cursor:pointer;
  text-align:left;max-width:100%;overflow:hidden;text-overflow:ellipsis;display:block}
.zg-link:hover{text-decoration:underline}
`;

/* ------------------------------------------------------------------ tooltip */

function useTooltip() {
  const [tt, setTt] = useState(null);
  const box = useRef(null);
  const mostrar = (e, conteudo) => {
    const r = box.current.getBoundingClientRect();
    setTt({ x: e.clientX - r.left, y: e.clientY - r.top, largura: r.width, conteudo });
  };
  return { tt, box, mostrar, esconder: () => setTt(null) };
}

function Tooltip({ tt }) {
  if (!tt) return null;
  const flip = tt.x > tt.largura - 170;
  return (
    <div className="zg-tt" style={{
      left: flip ? tt.x - 12 : tt.x + 12,
      top: tt.y - 10,
      transform: flip ? 'translateX(-100%)' : 'none',
    }}>{tt.conteudo}</div>
  );
}

/* ---------------------------------------------- saídas por grupo, dia a dia */

const METRICAS_GRUPO = [
  { id: 'saidas', rotulo: 'Saídas' },
  { id: 'entradas', rotulo: 'Entradas' },
  { id: 'saldo', rotulo: 'Saldo' },
];

const valorDe = (d, metrica) => (metrica === 'saldo' ? d.entradas - d.saidas : d[metrica]);

function corDaCelula(v, max, metrica) {
  if (!v) return undefined;
  const cor = metrica === 'entradas' ? 'var(--entrada)'
    : metrica === 'saidas' ? 'var(--saida)'
    : v > 0 ? 'var(--bom)' : 'var(--critico)';
  const pct = Math.round(12 + (Math.abs(v) / max) * 43);
  return `color-mix(in srgb, ${cor} ${pct}%, transparent)`;
}

function somarGrupos(grupos) {
  const contagens = grupos.map((g) => g.membrosAtuais).filter((v) => v != null);
  return {
    entradas: grupos.reduce((acc, g) => acc + g.entradas, 0),
    saidas: grupos.reduce((acc, g) => acc + g.saidas, 0),
    membrosAtuais: contagens.length ? contagens.reduce((acc, v) => acc + v, 0) : null,
    serie: grupos[0].serie.map((d, i) => ({
      dia: d.dia,
      entradas: grupos.reduce((acc, g) => acc + g.serie[i].entradas, 0),
      saidas: grupos.reduce((acc, g) => acc + g.serie[i].saidas, 0),
    })),
  };
}

function PorGrupoDiario({ porGrupo, instancias, onSelecionar }) {
  const [metrica, setMetrica] = useState('saidas');

  if (!porGrupo.length) {
    return (
      <div className="zg-card">
        <h2>Por grupo, dia a dia</h2>
        <p className="sub">Nenhum grupo nessa seleção. Um grupo aparece aqui depois do primeiro evento do webhook.</p>
      </div>
    );
  }

  const fmtValor = metrica === 'saldo' ? sinal : fmt;
  const rotuloMetrica = METRICAS_GRUPO.find((m) => m.id === metrica).rotulo.toLowerCase();
  const dias = porGrupo[0].serie.map((d) => d.dia);
  // mais recente à esquerda: é o dia que você quer ver sem rolar
  const ordem = dias.map((_, i) => dias.length - 1 - i);
  const max = Math.max(1, ...porGrupo.flatMap((g) => g.serie.map((d) => Math.abs(valorDe(d, metrica)))));

  const nomes = new Map(instancias.map((i) => [i.instanceId, rotuloInstancia(i)]));
  const porInstancia = new Map();
  for (const g of porGrupo) porInstancia.set(g.instanceId, [...(porInstancia.get(g.instanceId) || []), g]);

  const blocos = [...porInstancia.entries()]
    .map(([instanceId, grupos]) => ({
      instanceId,
      rotulo: nomes.get(instanceId) || rotuloInstancia({ instanceId }),
      // pior primeiro: mais saídas / mais entradas / menor saldo
      grupos: [...grupos].sort((a, b) =>
        (metrica === 'saldo' ? valorDe(a, metrica) - valorDe(b, metrica) : valorDe(b, metrica) - valorDe(a, metrica))
        || a.nome.localeCompare(b.nome, 'pt-BR')),
      subtotal: somarGrupos(grupos),
    }))
    .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR'));

  const mostrarSubtotal = porGrupo.length > 1;

  return (
    <div className="zg-card">
      <div className="zg-topo">
        <div>
          <h2>Por grupo, dia a dia</h2>
          <p className="sub">
            Quantas {rotuloMetrica} cada grupo teve em cada dia, separado por instância.
            Mais recente à esquerda; clique no grupo pra ver só ele.
          </p>
        </div>
        <div className="zg-filtros">
          {METRICAS_GRUPO.map((m) => (
            <button key={m.id} className="zg-chip" aria-pressed={metrica === m.id}
                    onClick={() => setMetrica(m.id)}>{m.rotulo}</button>
          ))}
        </div>
      </div>
      <div className="zg-scroll alto" style={{ marginTop: 10 }}>
        <table className="zg-mat">
          <thead><tr>
            <th className="fixa">Grupo</th><th>Membros</th><th>No período</th>
            {ordem.map((i) => <th key={dias[i]} className="dia">{rotuloDia(dias[i])}</th>)}
          </tr></thead>
          <tbody>
            {blocos.map((b) => (
              <React.Fragment key={b.instanceId}>
                {mostrarSubtotal && (
                  <tr className="inst">
                    <td className="fixa" title={b.rotulo}>
                      {b.rotulo} · {b.grupos.length} {b.grupos.length === 1 ? 'grupo' : 'grupos'}
                    </td>
                    <td>{fmt(b.subtotal.membrosAtuais)}</td>
                    <td>{fmtValor(valorDe(b.subtotal, metrica))}</td>
                    {ordem.map((i) => (
                      <td key={dias[i]} className="dia">{fmtValor(valorDe(b.subtotal.serie[i], metrica))}</td>
                    ))}
                  </tr>
                )}
                {b.grupos.map((g) => (
                  <tr key={g.groupId}>
                    <td className="fixa">
                      <button className="zg-link" title={`Ver só ${g.nome}`} onClick={() => onSelecionar(g)}>
                        {g.nome}
                      </button>
                    </td>
                    <td>{fmt(g.membrosAtuais)}</td>
                    <td className="tot">{fmtValor(valorDe(g, metrica))}</td>
                    {ordem.map((i) => {
                      const d = g.serie[i];
                      const v = valorDe(d, metrica);
                      return (
                        <td key={d.dia} className={v ? 'dia' : 'dia zero'}
                            style={{ background: corDaCelula(v, max, metrica) }}
                            title={`${g.nome} · ${rotuloDia(d.dia)}: ${d.saidas} saíram, ${d.entradas} entraram`}>
                          {fmtValor(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------------------------- gráfico 1: entradas x saídas */
/* Coordenadas em pixels (800x280) + preserveAspectRatio uniforme: o SVG escala
   junto com o container sem esticar o texto dos eixos. */

const W = 800, H = 280, P = { t: 16, r: 12, b: 30, l: 46 };
const PLOT_W = W - P.l - P.r;
const PLOT_H = H - P.t - P.b;

function ticksDe(max, n = 4) {
  const bruto = max / n;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.max(bruto, 1))));
  const passo = [1, 2, 2.5, 5, 10].find((m) => m * mag >= bruto) * mag;
  const out = [];
  for (let v = 0; v <= max + passo * 0.001; v += passo) out.push(Math.round(v));
  return out;
}

function BarrasEntradaSaida({ serie }) {
  const { tt, box, mostrar, esconder } = useTooltip();

  const max = Math.max(1, ...serie.map((d) => Math.max(d.entradas, d.saidas)));
  const ticks = ticksDe(max);
  const topo = ticks[ticks.length - 1];
  const passo = PLOT_W / Math.max(serie.length, 1);
  // 2px de respiro entre as duas barras do dia e entre os dias
  const larguraBarra = Math.max(2, (passo - 6) / 2);
  const esc = (v) => P.t + PLOT_H - (v / topo) * PLOT_H;
  const salto = Math.max(1, Math.ceil(serie.length / 12));

  return (
    <div className="zg-card">
      <h2>Entradas e saídas por dia</h2>
      <p className="sub">Soma da seleção. Quanto mais a laranja passa da azul, mais os grupos encolhem.</p>
      <div className="zg-legenda" style={{ margin: '10px 0 6px' }}>
        <span><i className="zg-dot" style={{ background: 'var(--entrada)' }} />Entradas</span>
        <span><i className="zg-dot" style={{ background: 'var(--saida)' }} />Saídas</span>
      </div>
      <div ref={box} style={{ position: 'relative' }} onMouseLeave={esconder}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
             style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
             aria-label="Gráfico de barras de entradas e saídas por dia">
          {ticks.map((v) => (
            <g key={v}>
              <line x1={P.l} x2={W - P.r} y1={esc(v)} y2={esc(v)}
                    stroke={v === 0 ? 'var(--axis)' : 'var(--grid)'} strokeWidth="1" />
              <text x={P.l - 8} y={esc(v) + 4} textAnchor="end"
                    style={{ fontSize: 11, fill: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{v}</text>
            </g>
          ))}

          {serie.map((d, i) => {
            const x0 = P.l + i * passo;
            const barras = [
              { v: d.entradas, cor: 'var(--entrada)', x: x0 + 2 },
              { v: d.saidas, cor: 'var(--saida)', x: x0 + 2 + larguraBarra + 2 },
            ];
            return (
              <g key={d.dia}
                 onMouseMove={(e) => mostrar(e, (
                   <>
                     <b>{rotuloDia(d.dia)}</b>
                     <div className="l"><i className="zg-dot" style={{ background: 'var(--entrada)' }} />Entradas<i>{d.entradas}</i></div>
                     <div className="l"><i className="zg-dot" style={{ background: 'var(--saida)' }} />Saídas<i>{d.saidas}</i></div>
                     <div className="l">Saldo<i>{sinal(d.liquido)}</i></div>
                   </>
                 ))}>
                <rect x={x0} y={P.t} width={passo} height={PLOT_H} fill="transparent" />
                {barras.map((b, k) => b.v > 0 && (
                  <rect key={k} x={b.x} y={esc(b.v)} width={larguraBarra}
                        height={Math.max(2, P.t + PLOT_H - esc(b.v))} rx="3" fill={b.cor} />
                ))}
                {i % salto === 0 && (
                  <text x={x0 + passo / 2} y={H - P.b + 16} textAnchor="middle"
                        style={{ fontSize: 11, fill: 'var(--muted)' }}>{rotuloDia(d.dia)}</text>
                )}
              </g>
            );
          })}
        </svg>
        <Tooltip tt={tt} />
      </div>
    </div>
  );
}

/* -------------------------------------------- gráfico 2: saldo acumulado */

function LinhaAcumulado({ serie }) {
  const { tt, box, mostrar, esconder } = useTooltip();
  const LVW = 460, LVH = 280;                      // viewBox próprio (card de meia largura)
  const L = { t: 18, r: 28, b: 30, l: 40 };
  const LW = LVW - L.l - L.r, LH = LVH - L.t - L.b;

  const vals = serie.map((d) => d.acumulado);
  const max = Math.max(0, ...vals), min = Math.min(0, ...vals);
  const span = (max - min) || 1;
  const x = (i) => L.l + (i / Math.max(serie.length - 1, 1)) * LW;
  const y = (v) => L.t + LH - ((v - min) / span) * LH;
  const caminho = serie.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p.acumulado).toFixed(1)}`).join(' ');

  const fim = serie[serie.length - 1];
  const positivo = fim && fim.acumulado >= 0;
  const salto = Math.max(1, Math.ceil(serie.length / 8));
  const marcas = [max, min + span / 2, min];

  return (
    <div className="zg-card">
      <h2>Saldo acumulado no período</h2>
      <p className="sub">Soma corrida de entradas menos saídas. Subindo, a seleção ganha gente; descendo, perde.</p>
      <div ref={box} style={{ position: 'relative', marginTop: 12 }} onMouseLeave={esconder}>
        <svg viewBox={`0 0 ${LVW} ${LVH}`} preserveAspectRatio="xMidYMid meet"
             style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
             aria-label="Linha do saldo acumulado de participantes">
          {marcas.map((v, i) => (
            <g key={i}>
              <line x1={L.l} x2={LVW - L.r} y1={y(v)} y2={y(v)} stroke="var(--grid)" strokeWidth="1" />
              <text x={L.l - 8} y={y(v) + 4} textAnchor="end"
                    style={{ fontSize: 10, fill: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(v)}
              </text>
            </g>
          ))}
          {min < 0 && max > 0 && (
            <line x1={L.l} x2={LVW - L.r} y1={y(0)} y2={y(0)} stroke="var(--axis)" strokeWidth="1" />
          )}

          <path d={caminho} fill="none" stroke="var(--saldo)" strokeWidth="2"
                strokeLinejoin="round" strokeLinecap="round" />

          {fim && (
            <>
              <circle cx={x(serie.length - 1)} cy={y(fim.acumulado)} r="4.5"
                      fill="var(--saldo)" stroke="var(--surface-1)" strokeWidth="2" />
              <text x={x(serie.length - 1) - 7} y={y(fim.acumulado) - 9} textAnchor="end"
                    style={{ fontSize: 11, fontWeight: 600, fill: 'var(--ink)' }}>
                {sinal(fim.acumulado)}
              </text>
            </>
          )}

          {serie.map((p, i) => (
            <g key={p.dia}
               onMouseMove={(e) => mostrar(e, (
                 <>
                   <b>{rotuloDia(p.dia)}</b>
                   <div className="l">Acumulado<i>{sinal(p.acumulado)}</i></div>
                   <div className="l">No dia<i>{sinal(p.liquido)}</i></div>
                 </>
               ))}>
              <rect x={x(i) - LW / serie.length / 2} y={L.t}
                    width={Math.max(4, LW / serie.length)} height={LH} fill="transparent" />
            </g>
          ))}

          {serie.map((p, i) => {
            const ultimo = i === serie.length - 1;
            const marcado = i % salto === 0;
            // só rotula o último se ele não encostar no rótulo anterior
            const sobra = (serie.length - 1) % salto;
            if (!(marcado || (ultimo && sobra > salto / 2))) return null;
            if (marcado && !ultimo && serie.length - 1 - i <= salto / 2 && sobra > salto / 2) return null;
            return (
              <text key={p.dia} x={x(i)} y={LVH - L.b + 15} textAnchor="middle"
                    style={{ fontSize: 10, fill: 'var(--muted)' }}>{rotuloDia(p.dia)}</text>
            );
          })}
        </svg>
        <Tooltip tt={tt} />
      </div>
      {fim && (
        <p className="sub" style={{ marginTop: 6, color: positivo ? 'var(--bom)' : 'var(--critico)' }}>
          {positivo ? '▲' : '▼'} {sinal(fim.acumulado)} pessoas no período
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------- gráfico 3: motivos (barra h) */

function Motivos({ porMotivo }) {
  const linhas = [
    { rot: 'Entrou pelo link', v: porMotivo.convite, cor: 'var(--entrada)' },
    { rot: 'Adicionado por admin', v: porMotivo.adicionado, cor: 'var(--entrada)' },
    { rot: 'Saiu sozinho', v: porMotivo.saiu, cor: 'var(--saida)' },
    { rot: 'Removido por admin', v: porMotivo.removido, cor: 'var(--saida)' },
  ];
  const max = Math.max(1, ...linhas.map((l) => l.v));
  return (
    <div className="zg-card">
      <h2>Por que entraram e por que saíram</h2>
      <p className="sub">“Saiu sozinho” é o número que dói: foi decisão da pessoa, não sua.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
        {linhas.map((l) => (
          <div key={l.rot}>
            <div style={{ display: 'flex', fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="zg-dot" style={{ background: l.cor }} />{l.rot}
              </span>
              <b style={{ marginLeft: 'auto', color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>{fmt(l.v)}</b>
            </div>
            <div style={{ height: 8, background: 'var(--grid)', borderRadius: 4 }}>
              <div style={{ width: `${(l.v / max) * 100}%`, height: '100%',
                            background: l.cor, borderRadius: 4 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------- quem entrou e quem saiu */

const ROTULO_MOTIVO = {
  convite: 'entrou pelo link', adicionado: 'adicionado por admin',
  saiu: 'saiu sozinho', removido: 'removido por admin',
};

const FILTROS_TIPO = [
  { id: 'todos', rotulo: 'Todos' },
  { id: 'entrada', rotulo: 'Entraram' },
  { id: 'saida', rotulo: 'Saíram' },
];

function Eventos({ eventos, mostrarGrupo }) {
  const [tipo, setTipo] = useState('todos');
  const [busca, setBusca] = useState('');

  const visiveis = useMemo(() => {
    const q = busca.replace(/\D/g, '');
    return eventos.filter((e) =>
      (tipo === 'todos' || e.tipo === tipo) && (!q || String(e.participante).includes(q)));
  }, [eventos, tipo, busca]);

  return (
    <div className="zg-card">
      <h2>Quem entrou e quem saiu</h2>
      <p className="sub">
        {visiveis.length === eventos.length
          ? `${fmt(eventos.length)} movimentações no período`
          : `${fmt(visiveis.length)} de ${fmt(eventos.length)} movimentações`}
        {eventos.length >= LIMITE_EVENTOS && ` · mostrando só as ${LIMITE_EVENTOS} mais recentes`}
      </p>
      <div className="zg-filtros" style={{ marginTop: 10 }}>
        {FILTROS_TIPO.map((f) => (
          <button key={f.id} className="zg-chip" aria-pressed={tipo === f.id}
                  onClick={() => setTipo(f.id)}>{f.rotulo}</button>
        ))}
        <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)}
               placeholder="Buscar telefone" aria-label="Buscar telefone" />
      </div>
      <div className="zg-scroll alto" style={{ marginTop: 10 }}>
        <table className="zg-tab">
          <thead><tr>
            <th>Quando</th>{mostrarGrupo && <th>Grupo</th>}<th>Telefone</th><th>O quê</th>
          </tr></thead>
          <tbody>
            {visiveis.map((e) => (
              <tr key={`${e.groupId}/${e.id}`}>
                <td>{new Date(e.momentoMs).toLocaleString('pt-BR', {
                  dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo',
                })}</td>
                {mostrarGrupo && <td>{e.grupoNome || e.groupId}</td>}
                <td>{e.participante}</td>
                <td>
                  <span className="zg-tag">
                    <i className="zg-dot" style={{ background: e.tipo === 'entrada' ? 'var(--entrada)' : 'var(--saida)' }} />
                    {ROTULO_MOTIVO[e.motivo] || e.motivo}
                  </span>
                </td>
              </tr>
            ))}
            {!visiveis.length && (
              <tr><td colSpan={mostrarGrupo ? 4 : 3}>Sem movimentações no período.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- tabela dos dados */

function TabelaSerie({ serie }) {
  return (
    <div className="zg-card">
      <h2>Dados do gráfico</h2>
      <div className="zg-scroll" style={{ marginTop: 10 }}>
        <table className="zg-tab">
          <thead><tr><th>Dia</th><th>Entradas</th><th>Saídas</th><th>Saldo</th><th>Acumulado</th></tr></thead>
          <tbody>
            {serie.map((d) => (
              <tr key={d.dia}>
                <td>{d.dia}</td><td>{d.entradas}</td><td>{d.saidas}</td>
                <td>{sinal(d.liquido)}</td><td>{sinal(d.acumulado)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- KPI */

function Kpi({ rotulo, valor, pe, cor, dot }) {
  return (
    <div className="zg-card zg-kpi">
      <div className="rot">
        {dot && <i className="zg-dot" style={{ background: dot }} />}{rotulo}
      </div>
      <div className="val" style={cor ? { color: cor } : undefined}>{valor}</div>
      {pe && <div className="pe">{pe}</div>}
    </div>
  );
}

/* ------------------------------------------------------------- componente */

export default function DashboardGrupos({ apiBase = '', dadosIniciais = null, eventosIniciais = [] }) {
  const [instanceId, setInstanceId] = useState('all');
  const [groupId, setGroupId] = useState('all');
  const [dias, setDias] = useState(30);
  const [de, setDe] = useState(diasAtras(29));
  const [ate, setAte] = useState(HOJE());
  const [dados, setDados] = useState(dadosIniciais);
  const [eventos, setEventos] = useState(eventosIniciais);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(!dadosIniciais);
  const [verTabela, setVerTabela] = useState(false);

  const aplicarPreset = (n) => { setDias(n); setDe(diasAtras(n - 1)); setAte(HOJE()); };

  useEffect(() => {
    let vivo = true;
    setCarregando(true); setErro(null);
    const q = `instanceId=${encodeURIComponent(instanceId)}&groupId=${encodeURIComponent(groupId)}&de=${de}&ate=${ate}`;
    Promise.all([
      getJson(`${apiBase}/group-members/metrics?${q}`),
      getJson(`${apiBase}/group-members/events?${q}&limit=${LIMITE_EVENTOS}`),
    ])
      .then(([m, e]) => {
        if (!vivo) return;
        setDados(m); setEventos(e.eventos || []);
      })
      .catch((err) => vivo && setErro(err.message))
      .finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
  }, [apiBase, instanceId, groupId, de, ate]);

  const grupos = [...(dados?.grupos || [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  const instancias = [...(dados?.instancias || [])]
    .sort((a, b) => rotuloInstancia(a).localeCompare(rotuloInstancia(b), 'pt-BR'));
  const gruposDaInstancia = instanceId === 'all' ? grupos : grupos.filter((g) => g.instanceId === instanceId);

  const grupoAtual = grupos.find((g) => g.groupId === groupId);
  const instanciaAtual = instancias.find((i) => i.instanceId === (grupoAtual?.instanceId || instanceId));
  const titulo = groupId !== 'all' ? (grupoAtual?.nome || groupId)
    : instanceId !== 'all' ? rotuloInstancia(instanciaAtual || { instanceId })
    : 'Todas as instâncias';

  const trocarInstancia = (id) => { setInstanceId(id); setGroupId('all'); };
  const verGrupo = (g) => { setInstanceId(g.instanceId); setGroupId(g.groupId); };

  const t = dados?.totais;

  return (
    <div className="zg">
      <style>{CSS}</style>
      <div className="zg-wrap">

        <header>
          <h1>Entradas e saídas · {titulo}</h1>
          <p className="sub">
            {groupId !== 'all' && instanciaAtual && `${rotuloInstancia(instanciaAtual)} · `}
            {dados ? `${rotuloDia(dados.periodo.de)} a ${rotuloDia(dados.periodo.ate)} · ${dados.periodo.dias} dias` : 'carregando…'}
            {carregando && dados && ' · atualizando…'}
          </p>
        </header>

        <div className="zg-card zg-filtros">
          <select value={instanceId} onChange={(e) => trocarInstancia(e.target.value)} aria-label="Instância">
            <option value="all">Todas as instâncias</option>
            {instancias.map((i) => (
              <option key={i.instanceId} value={i.instanceId}>
                {rotuloInstancia(i)} ({i.grupos} {i.grupos === 1 ? 'grupo' : 'grupos'})
              </option>
            ))}
          </select>
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)} aria-label="Grupo">
            <option value="all">{instanceId === 'all' ? 'Todos os grupos' : 'Todos os grupos da instância'}</option>
            {gruposDaInstancia.map((g) => <option key={g.groupId} value={g.groupId}>{g.nome}</option>)}
          </select>
          {PRESETS.map((p) => (
            <button key={p.id} className="zg-chip" aria-pressed={dias === p.id}
                    onClick={() => aplicarPreset(p.id)}>{p.rotulo}</button>
          ))}
          <input type="date" value={de} max={ate}
                 onChange={(e) => { setDe(e.target.value); setDias(0); }} aria-label="Data inicial" />
          <input type="date" value={ate} min={de} max={HOJE()}
                 onChange={(e) => { setAte(e.target.value); setDias(0); }} aria-label="Data final" />
          <button className="zg-chip" aria-pressed={verTabela} style={{ marginInlineStart: 'auto' }}
                  onClick={() => setVerTabela((v) => !v)}>
            {verTabela ? 'Ver gráficos' : 'Ver tabela'}
          </button>
        </div>

        {erro && <div className="zg-card zg-erro">Não consegui carregar: {erro}</div>}
        {carregando && !dados && <div className="zg-card">Carregando…</div>}

        {dados && (
          <>
            <div className="zg-kpis">
              <Kpi rotulo="Entradas" dot="var(--entrada)" valor={fmt(t.entradas)}
                   pe={`${t.entradasPorDia}/dia`} />
              <Kpi rotulo="Saídas" dot="var(--saida)" valor={fmt(t.saidas)}
                   pe={`${t.saidasPorDia}/dia`} />
              <Kpi rotulo="Saldo do período" valor={sinal(t.liquido)}
                   cor={t.liquido >= 0 ? 'var(--bom)' : 'var(--critico)'}
                   pe={t.razaoEntradaSaida != null ? `${t.razaoEntradaSaida} entradas por saída` : 'nenhuma saída'} />
              <Kpi rotulo="Membros hoje" valor={fmt(t.membrosAtuais)}
                   pe={t.membrosAtuais == null ? 'sem contagem ao vivo' : 'contagem ao vivo da Z-API'} />
              <Kpi rotulo="Churn do período" valor={t.churnPct != null ? `${t.churnPct}%` : '—'}
                   pe="saídas ÷ membros" />
            </div>

            <PorGrupoDiario porGrupo={dados.porGrupo || []} instancias={instancias} onSelecionar={verGrupo} />

            {verTabela ? <TabelaSerie serie={dados.serie} /> : (
              <>
                <BarrasEntradaSaida serie={dados.serie} />
                <div className="zg-grid2">
                  <LinhaAcumulado serie={dados.serie} />
                  <Motivos porMotivo={dados.porMotivo} />
                </div>
              </>
            )}

            <Eventos eventos={eventos} mostrarGrupo={groupId === 'all'} />
          </>
        )}
      </div>
    </div>
  );
}
